import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DxDataGridComponent } from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { SalonMappingService } from '../../../api/salon-mapping.api';
import { NotificationService } from '../../../service/notification.service';
import { NotificationType } from '../../../shared/enum/notification.enum';
import { MappingAction, MappingMode } from '../../../shared/enum/mapping-mode.enum';
import { LoadOptions, ExtraFilteringOptions } from '../../../shared/models/shared.model';
import {
	SalonMappingGridModel,
	SalonMappingToMasterGridModel,
	SalonMasterWithReportRegionListGridModel
} from '../../../shared/models/salon-mapping.model';
import { UserApi } from '../../../api';
import { User, GridSetting, GridSettings, GridState } from '../../../shared';
import { FilterBuilderService } from '../../../service/filter-builder.service';
import { LoaderService } from '../../../core/loader/loader.service';

@Component({
  selector: 'app-salon-mapping',
  templateUrl: './salon-mapping.component.html',
  styleUrls: ['./salon-mapping.component.css']
})
export class SalonMappingComponent implements OnInit, OnDestroy {
  @ViewChild('salonMappingGrid') salonMappingGrid: DxDataGridComponent; // top grid
  @ViewChild('mappingToMasterGrid') mappingToMasterGrid: DxDataGridComponent; // middle grid
  @ViewChild('salonMasterGrid') salonMasterGrid: DxDataGridComponent; // bottom grid

  public salonMappingDataSource: any = {};
  public selectedMappingRow: SalonMappingGridModel;
  public mappingToMasterData: SalonMappingToMasterGridModel[] = [];
  public salonMasterDataSource: any = {};
  public currentUser: User;
  public isLoadSalonMappingGridDataSource: boolean;
  public isLoadSalonMasterGridDataSource: boolean;
  public isSalonMappingDataLoading: boolean = false;
  public isSalonMappingToMasterDataLoading: boolean = false;
  public isSalonMasterDataLoading: boolean = false;
  public isGrid1DataReady: boolean = false;
  public isGrid2Disabled: boolean = false;
  public needsGrid2Update: boolean = false;

  // Banding visibility properties
  public showDataProviderTypeBanding: boolean = false;
  public showSourceDataSalonChainBanding: boolean = false;

  // Extra filtering options removed - not supported by salon mapping API

  // Header filter data sources
  public mappingAssignedHeaderDataSource;
  public modifiedByHeaderDataSource;
  public reviewedByHeaderDataSource;
  public createdByHeaderDataSource;
  public dataProviderNameHeaderDataSource;
  public mappingPriorityHeaderDataSource;
  public mappingModeNameHeaderDataSource;
  public modifiedOnHeaderDataSource;
  public reviewedOnHeaderDataSource;
  public createdOnHeaderDataSource;
  public sourceCountryHeaderDataSource;
  public sourceCountrySubdivisionHeaderDataSource;
  public sourceSalonTypeHeaderDataSource;
  public dataProviderHeaderDataSource;
  public primaryDataProviderHeaderDataSource;
  public baseLevelGeographicalEntityHeaderDataSource;

  // Grid settings
  private readonly mappingGridName: string = 'salonMappingDataGrid';
  private readonly pageName: string = 'salonMapping';
  private currentSalonMappingDataGridState: GridState;
  private salonMappingDataGridSettings: GridSettings;
  private salonMappingDataGridSetting: GridSetting = new GridSetting();
  private isSalonMappingDataGridSettingLoading = true;

  private destroyed$ = new Subject<void>();

  constructor(
    private salonMappingService: SalonMappingService,
    private notificationService: NotificationService,
    private userService: UserApi,
    private filterBuilder: FilterBuilderService,
    private loaderService: LoaderService
  ) {}

  ngOnInit(): void {
    this.loaderService.show();
    this.currentUser = this.userService.logonUser;
    if (!this.currentUser) {
      return;
    }
    
    // Initialize Grid 2 as disabled until Grid 1 is ready
    this.isGrid2Disabled = true;
    
    // Initialize mapping mode data source first
    this.mappingModeNameHeaderDataSource = [
      {
        text: 'New',
        value: 0
      },
      {
        text: 'Mapped',
        value: 1
      },
      {
        text: 'Saved',
        value: 2
      },
      {
        text: 'Omitted',
        value: 3
      }
    ];
    
    this.loadColumnHeaderFilterData();
    
    // Load Grid 3 (bottom grid) immediately - it's independent
    this.loadBottomGrid();
    
    this.salonMappingDataGridSettings = {
      PageName: this.pageName,
      GridName: this.mappingGridName,
      OwnedByUserId: this.currentUser.UserId
    };
    
    this.userService.getGridSettings(this.salonMappingDataGridSettings)
      .pipe(take(1))
      .subscribe((settings) => {
        if (settings && settings.Settings) {
          if (typeof settings.Settings === 'string') {
            this.salonMappingDataGridSetting = JSON.parse(settings.Settings);
          } else {
            this.salonMappingDataGridSetting = settings.Settings;
          }
        } else {
          this.salonMappingDataGridSettings.Settings = JSON.stringify({autosave: true});
          this.updateGridSettings(this.salonMappingDataGridSettings);
        }
        this.loadSalonMappingDataGridState();
      });
  }

  private loadSalonMappingDataGridState() {
    const salonMappingDataGridState: GridState = {
      PageName: this.pageName,
      GridName: this.mappingGridName,
      OwnedByUserId: this.currentUser.UserId
    };
    this.userService.getGridState(salonMappingDataGridState)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((state) => {
        if (state) {
          this.currentSalonMappingDataGridState = state;
        } else {
          this.currentSalonMappingDataGridState = {
            PageName: this.pageName,
            GridName: this.mappingGridName,
            OwnedByUserId: this.currentUser.UserId,
            State: null
          };
        }
        this.loadTopGrid();
      });
  }

  private loadTopGrid(): void {
    this.isSalonMappingDataGridSettingLoading = false;
    this.salonMappingDataSource = new CustomStore({
      key: 'SalonMappingId',
      load: (loadOptions: any) => {
        this.loaderService.show();
        this.isSalonMappingDataLoading = true;
        const finalOptions: LoadOptions = loadOptions;
        
        // Process the filter to convert MappingModeId text search to numeric IDs
        let filter = loadOptions.filter;
        if (filter && Array.isArray(filter)) {
          console.log('Processing filter in loadTopGrid:', JSON.stringify(filter, null, 2));
          filter = this.processFilterForMappingMode(filter);
          console.log('Processed filter in loadTopGrid:', JSON.stringify(filter, null, 2));
        }
        
        // Format filters to match backend expectations
        filter = this.formatFilterOperators(filter);
        finalOptions.filter = filter;
        // Remove extraParams for salon mapping API - not needed
        delete finalOptions.extraParams;
        
        return this.salonMappingService.getSalonMappingGrid(finalOptions)
          .toPromise()
          .then((data: any) => {
            console.log('Salon mapping grid data loaded:', data);
            this.isSalonMappingDataLoading = false;
            this.isGrid1DataReady = true; // Mark Grid 1 data as ready
            this.loaderHandler();
            
            // Smart row selection logic
            if (this.selectedMappingRow) {
              const matchedData = data.data.find(item =>
                item.SalonMappingId === this.selectedMappingRow.SalonMappingId);
              if (matchedData) {
                // Row still exists, keep selection
                console.log('Selected row still exists, keeping selection:', this.selectedMappingRow.SalonMappingId);
                // Update the selected row data with fresh data
                this.selectedMappingRow = matchedData;
              } else {
                // Row no longer exists, select first row
                console.log('Selected row no longer exists, will select first row');
                this.selectedMappingRow = null;
              }
            }
            
            if (!data.data || data.data.length === 0) {
              this.mappingToMasterData = [];
            }
            
            // Grid 1 data is ready - force first row selection
            console.log('Grid 1 data ready, forcing first row selection');
            setTimeout(() => {
              this.forceFirstRowSelectionAndUpdateGrid2();
            }, 100);
            
            return {
              data: data.data,
              totalCount: data.totalCount,
              summary: data.summary,
              groupCount: data.groupCount
            };
          })
          .catch(error => {
            console.error('Error loading salon mapping grid data:', error);
            this.isSalonMappingDataLoading = false;
            this.loaderHandler();
            throw new Error('Data Loading Error');
          });
      }
    });
    this.isLoadSalonMappingGridDataSource = true;
  }

  private loadBottomGrid(): void {
    this.salonMasterDataSource = new CustomStore({
      key: 'SalonMasterId',
      load: (loadOptions: any) => {
        this.loaderService.show();
        this.isSalonMasterDataLoading = true;
        const finalOptions: LoadOptions = loadOptions;
        // Format filters to match backend expectations
        let filter = this.formatFilterOperators(loadOptions.filter);
        finalOptions.filter = filter;
        // Remove extraParams for salon mapping API - not needed
        delete finalOptions.extraParams;
        
        return this.salonMappingService.getSalonMasterWithReportRegionListGrid(finalOptions)
          .toPromise()
          .then((data: any) => {
            this.isSalonMasterDataLoading = false;
            this.loaderHandler();
            return {
              data: data.data,
              totalCount: data.totalCount,
              summary: data.summary,
              groupCount: data.groupCount
            };
          })
          .catch(error => {
            this.isSalonMasterDataLoading = false;
            this.loaderHandler();
            throw new Error('Data Loading Error');
          });
      }
    });
    this.isLoadSalonMasterGridDataSource = true;
  }

  selectionChangedHandler(): void {
    console.log('selectionChangedHandler called');
    
    if (this.salonMappingGrid?.instance?.getSelectedRowsData()?.[0]) {
      this.selectedMappingRow = this.salonMappingGrid.instance.getSelectedRowsData()[0];
      const salonMappingId = this.selectedMappingRow.SalonMappingId;
      console.log('Selected row SalonMappingId:', salonMappingId);
      console.log('Selected row data:', this.selectedMappingRow);
      if (salonMappingId) {
        console.log('Triggering Grid 2 update for SalonMappingId:', salonMappingId);
        this.getSalonMappingToMasterGridResult(salonMappingId);
      }
    } else {
      console.log('No selected row found in selectionChangedHandler');
    }
  }

  onFilterChanged(): void {
    console.log('Filter changed - processing MappingModeId text search');
    
    // Get the current filter
    const filter = this.salonMappingGrid.instance.getCombinedFilter();
    console.log('Current filter:', JSON.stringify(filter, null, 2));
    
    // Process the filter to convert text to IDs for MappingModeId
    if (filter && Array.isArray(filter)) {
      const processedFilter = this.processFilterForMappingMode(filter);
      console.log('Processed filter:', JSON.stringify(processedFilter, null, 2));
      
      // Apply the processed filter if it changed
      if (JSON.stringify(processedFilter) !== JSON.stringify(filter)) {
        console.log('Filter changed, applying processed filter');
        this.salonMappingGrid.instance.filter(processedFilter);
        return; // Don't refresh yet, let the filter change trigger another onFilterChanged
      }
    }
    
    console.log('No MappingModeId text search found, proceeding with normal refresh');
    
    // Clear the current selection to ensure fresh data
    this.selectedMappingRow = null;
    this.mappingToMasterData = [];
    
    // Refresh Grid 1 and force first row selection
    this.salonMappingGrid.instance.refresh().then(() => {
      console.log('Grid 1 refreshed, forcing first row selection');
      this.forceFirstRowSelectionAndUpdateGrid2();
    });
  }

  getSalonMappingToMasterGridResult(salonMappingId: number): void {
    console.log('getSalonMappingToMasterGridResult called with salonMappingId:', salonMappingId);
    
    if (!salonMappingId) {
      console.log('No salonMappingId provided, returning');
      return;
    }
    
    this.loaderService.show();
    this.isSalonMappingToMasterDataLoading = true;
    const loadOptions: LoadOptions = {
      filter: [],
      requireTotalCount: true,
      skip: 0,
      take: 25,
      sort: [{ selector: 'RankingPosition', desc: false }]
      // No extraParams needed for salon mapping API
    };
    this.salonMappingService.getSalonMappingToMasterGrid(salonMappingId, loadOptions)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data: any) => {
        this.isSalonMappingToMasterDataLoading = false;
        this.loaderHandler();
        this.mappingToMasterData = data.data || [];
        if (this.mappingToMasterGrid?.instance) {
          this.mappingToMasterGrid.instance.option('dataSource', data.data || []);
        }
        console.log('Grid 2 loaded with fresh data:', data.data?.length || 0, 'items');
      }, (error) => {
        this.isSalonMappingToMasterDataLoading = false;
        this.loaderHandler();
        console.error('Error loading salon mapping to master grid:', error);
      });
  }

  // Actions
  activateMapping(row: any): void {
    this.loaderService.show();
    this.salonMappingService.mappingAction({
      ActionType: MappingAction.Reactivate,
      SalonMappingId: row.SalonMappingId,
      ModifiedByUserId: this.currentUser.UserId,
      MappingModeId: MappingMode.Mapped,
      Active: true
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.loaderHandler();
        this.notificationService.show('Activated mapping', NotificationType.Success);
        this.salonMappingGrid?.instance?.refresh();
      });
  }

  deactivateMapping(row: any): void {
    this.loaderService.show();
    this.salonMappingService.mappingAction({ 
      ActionType: MappingAction.Deactivate, 
      SalonMappingId: row.SalonMappingId,
      ModifiedByUserId: this.currentUser.UserId,
      MappingModeId: MappingMode.Mapped,
      Active: false
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.loaderHandler();
        this.notificationService.show('Deactivated mapping', NotificationType.Success);
        this.salonMappingGrid?.instance?.refresh();
      });
  }

  accept(row: any): void {
    this.loaderService.show();
    this.salonMappingService.mappingAction({ 
      ActionType: MappingAction.Accept, 
      SalonMappingId: row.SalonMappingId,
      ModifiedByUserId: this.currentUser.UserId,
      MappingModeId: 1
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.loaderHandler();
        this.notificationService.show('Accepted', NotificationType.Success);
        this.refreshMappingDataGridPreserveFilters();
      });
  }

  save(row: any): void {
    this.loaderService.show();
    this.salonMappingService.mappingAction({ 
      ActionType: MappingAction.Save, 
      SalonMappingId: row.SalonMappingId,
      ModifiedByUserId: this.currentUser.UserId,
      MappingModeId: MappingMode.Saved
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.loaderHandler();
        this.notificationService.show('Saved', NotificationType.Success);
        this.refreshMappingDataGridPreserveFilters();
      });
  }

  omit(row: any): void {
    this.loaderService.show();
    this.salonMappingService.mappingAction({ 
      ActionType: MappingAction.Omit, 
      SalonMappingId: row.SalonMappingId,
      ModifiedByUserId: this.currentUser.UserId,
      MappingModeId: MappingMode.Omitted
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.loaderHandler();
        this.notificationService.show('Omitted', NotificationType.Success);
        this.refreshMappingDataGridPreserveFilters();
      });
  }

  review(row: any): void {
    this.loaderService.show();
    this.salonMappingService.mappingAction({ 
      ActionType: MappingAction.Review, 
      SalonMappingId: row.SalonMappingId,
      ModifiedByUserId: this.currentUser.UserId
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.loaderHandler();
        this.notificationService.show('Marked for review', NotificationType.Success);
        this.refreshMappingDataGridPreserveFilters();
      });
  }

  acceptMapping(salonMasterId: number): void {
    this.loaderService.show();
    this.salonMappingService.mappingAction({ 
      ActionType: MappingAction.AcceptMapping, 
      SalonMappingId: this.selectedMappingRow.SalonMappingId,
      SalonMasterId: salonMasterId,
      ModifiedByUserId: this.currentUser.UserId,
      MappingModeId: MappingMode.Mapped
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.loaderHandler();
        this.notificationService.show('Mapping accepted', NotificationType.Success);
        this.refreshMappingDataGridPreserveFilters();
        if (this.selectedMappingRow?.SalonMappingId) {
          this.getSalonMappingToMasterGridResult(this.selectedMappingRow.SalonMappingId);
        }
      });
  }

  mapToMaster(masterRow: any): void {
    if (!this.selectedMappingRow?.SalonMappingId || !masterRow?.SalonMasterId) {
      return;
    }
    this.loaderService.show();
    this.salonMappingService.mappingAction({ 
      ActionType: MappingAction.Map, 
      SalonMappingId: this.selectedMappingRow.SalonMappingId, 
      SalonMasterId: masterRow.SalonMasterId,
      ModifiedByUserId: this.currentUser.UserId,
      MappingModeId: 1
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.loaderHandler();
        this.notificationService.show('Mapped to master', NotificationType.Success);
        this.refreshMappingDataGridPreserveFilters();
      });
  }

  activateMaster(row: any): void {
    this.loaderService.show();
    this.salonMappingService.mappingAction({ 
      ActionType: MappingAction.Reactivate, 
      SalonMasterId: row.SalonMasterId,
      ModifiedByUserId: this.currentUser.UserId,
      Active: true
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.loaderHandler();
        this.notificationService.show('Activated master', NotificationType.Success);
        this.salonMasterGrid?.instance?.refresh();
      });
  }

  deactivateMaster(row: any): void {
    this.loaderService.show();
    this.salonMappingService.mappingAction({ 
      ActionType: MappingAction.Deactivate, 
      SalonMasterId: row.SalonMasterId,
      ModifiedByUserId: this.currentUser.UserId,
      Active: false
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.loaderHandler();
        this.notificationService.show('Deactivated master', NotificationType.Success);
        this.salonMasterGrid?.instance?.refresh();
      });
  }

  // Grid state management
  public loadMappingState = () => {
    return this.currentSalonMappingDataGridState.State ? JSON.parse(this.currentSalonMappingDataGridState.State) : null;
  }

  public saveMappingState = (state) => {
    if (this.salonMappingDataGridSetting) {
      let gridSetting: GridSetting;
      if (typeof this.salonMappingDataGridSetting === 'string') {
        gridSetting = JSON.parse(this.salonMappingDataGridSetting);
      } else {
        gridSetting = this.salonMappingDataGridSetting;
      }
      if (gridSetting.autosave) {
        this.currentSalonMappingDataGridState.State = JSON.stringify(state);
        this.updateGridState(this.currentSalonMappingDataGridState);
      }
    }
  }

  private updateGridSettings(gridSettings: GridSettings) {
    gridSettings.SavedOn = new Date();
    this.userService.setGridSettings(gridSettings).pipe(take(1)).subscribe();
  }

  private updateGridState(gridState: GridState) {
    gridState.SavedOn = new Date();
    this.userService.setGridState(gridState).pipe(take(1)).subscribe();
  }

  // Context menus
  salonMappingDataContextMenu(e) {
    e.event.stopPropagation();
    if (e.target === 'content') {
      if (!e.items) { e.items = []; }
      e.items.push(
        {
          text: 'Refresh',
          icon: 'refresh',
          onItemClick: () => {
            this.refreshMappingDataGrid();
          }
        },
        {
          text: 'Grid Settings',
          items: [
            {
              text: this.salonMappingDataGridSetting.autosave ? 'Disable autosave' : 'Enable autosave',
              onItemClick: () => {
                this.salonMappingDataGridSetting.autosave = !this.salonMappingDataGridSetting.autosave;
                this.salonMappingDataGridSettings.Settings = JSON.stringify(this.salonMappingDataGridSetting);
                this.updateGridSettings(this.salonMappingDataGridSettings);
              }
            },
            {
              text: 'Reset to Default',
              onItemClick: () => {
                this.userService.resetDataGridStateToDefault(this.mappingGridName, this.pageName, this.currentUser.UserId)
                  .pipe(take(1))
                  .subscribe(() => {
                    this.refreshMappingDataGrid();
                  });
              }
            }
          ]
        }
      );
    }
  }

  mappingToMasterContextMenu(e) {
    if (e.target === 'content') {
      if (!e.items) { e.items = []; }
      e.items.push(
        {
          text: 'Refresh',
          icon: 'refresh',
          onItemClick: () => {
            if (this.selectedMappingRow?.SalonMappingId) {
              this.getSalonMappingToMasterGridResult(this.selectedMappingRow.SalonMappingId);
            }
          }
        }
      );
    }
  }

  salonMasterContextMenu(e) {
    if (e.target === 'content') {
      if (!e.items) { e.items = []; }
      e.items.push(
        {
          text: 'Refresh',
          icon: 'refresh',
          onItemClick: () => {
            this.salonMasterGrid?.instance?.refresh();
          }
        }
      );
    }
  }

  // Utility methods
  customizeSalonMappingId(cellInfo) {
    return 'SaMp_' + cellInfo.value;
  }

  customizeSalonMasterId(cellInfo) {
    return 'SMs_' + cellInfo.value;
  }

  customizeMappingMode = (cellInfo) => {
    const mappingModeId = cellInfo.value;
    console.log('customizeMappingMode called with:', mappingModeId, 'DataSource:', this.mappingModeNameHeaderDataSource);
    
    if (!this.mappingModeNameHeaderDataSource) {
      console.log('mappingModeNameHeaderDataSource is not initialized yet');
      return String(mappingModeId);
    }
    
    const mappingMode = this.mappingModeNameHeaderDataSource.find(mode => mode.value === mappingModeId);
    const result = mappingMode ? mappingMode.text : String(mappingModeId);
    console.log('customizeMappingMode result:', result);
    return result;
  }

  // mappingModeCalculateFilterExpression removed per revert; default behavior used

  // Custom filter function to convert text search to numeric ID
  mappingModeFilter = (filterValue, selectedFilterOperation, target) => {
    console.log('mappingModeFilter called with:', filterValue, selectedFilterOperation, target);
    
    if (!filterValue || typeof filterValue !== 'string') {
      return [filterValue];
    }
    
    // Convert text search to numeric IDs using regex matching
    const searchText = filterValue.toLowerCase();
    const matchingIds = [];
    
    this.mappingModeNameHeaderDataSource.forEach(mode => {
      const modeText = mode.text.toLowerCase();
      if (modeText.includes(searchText)) {
        matchingIds.push(mode.value);
      }
    });
    
    console.log('mappingModeFilter converted text to IDs:', matchingIds);
    return matchingIds;
  }


  // Process filter to convert MappingModeId text search to numeric IDs
  processFilterForMappingMode = (filter) => {
    console.log('processFilterForMappingMode called with:', JSON.stringify(filter, null, 2));
    
    if (!Array.isArray(filter)) {
      console.log('Filter is not an array, returning as is');
      return filter;
    }
    
    // Check if this is a simple array format like ["MappingModeId", "contains", "Ma"]
    if (filter.length === 3 && typeof filter[0] === 'string' && typeof filter[1] === 'string' && typeof filter[2] === 'string') {
      console.log('Detected simple array format, checking for MappingModeId');
      
      if (filter[0] === 'MappingModeId' && filter[1] === 'contains') {
        console.log('Found MappingModeId contains filter, converting text to IDs');
        
        // Convert text search to numeric IDs
        const searchText = filter[2].toLowerCase();
        const matchingIds = [];
        
        console.log('Search text:', searchText);
        console.log('Available modes:', this.mappingModeNameHeaderDataSource);
        
        this.mappingModeNameHeaderDataSource.forEach(mode => {
          const modeText = mode.text.toLowerCase();
          console.log('Checking mode:', modeText, 'against search:', searchText);
          if (modeText.includes(searchText)) {
            matchingIds.push(mode.value);
            console.log('Match found:', modeText, '->', mode.value);
          }
        });
        
        console.log('Converting MappingModeId text search:', filter[2], 'to IDs:', matchingIds);
        
        if (matchingIds.length > 0) {
          const result = ['MappingModeId', 'contains', matchingIds[0]];
          console.log('Returning converted filter:', JSON.stringify(result, null, 2));
          return result;
        } else {
          console.log('No matching IDs found, returning original filter');
          return filter;
        }
      }
    }
    
    // Handle object format or nested arrays
    return filter.map(item => {
      console.log('Processing filter item:', JSON.stringify(item, null, 2));
      
      if (Array.isArray(item)) {
        console.log('Item is array, recursing');
        return this.processFilterForMappingMode(item);
      }
      
      if (item && item.column === 'MappingModeId' && item.type === 'string' && typeof item.value === 'string') {
        console.log('Found MappingModeId string filter, converting text to IDs');
        
        // Convert text search to numeric IDs
        const searchText = item.value.toLowerCase();
        const matchingIds = [];
        
        console.log('Search text:', searchText);
        console.log('Available modes:', this.mappingModeNameHeaderDataSource);
        
        this.mappingModeNameHeaderDataSource.forEach(mode => {
          const modeText = mode.text.toLowerCase();
          console.log('Checking mode:', modeText, 'against search:', searchText);
          if (modeText.includes(searchText)) {
            matchingIds.push(mode.value);
            console.log('Match found:', modeText, '->', mode.value);
          }
        });
        
        console.log('Converting MappingModeId text search:', item.value, 'to IDs:', matchingIds);
        
        if (matchingIds.length > 0) {
          const result = {
            ...item,
            type: 'string',
            value: matchingIds[0].toString(),
            operation: 'contains'
          };
          console.log('Returning converted filter:', JSON.stringify(result, null, 2));
          return result;
        } else {
          console.log('No matching IDs found, returning original item');
          return item;
        }
      }
      
      console.log('Item does not match MappingModeId criteria, returning as is');
      return item;
    });
  }

  // extraFilteringOptionsChange method removed - not needed for salon mapping

  formatFilterOperators(rawFilter: any[]) {
    if (!rawFilter) {
      return null;
    }
    const filterData = this.filterBuilder.handleNotFilterFormatting(rawFilter);
    return this.filterBuilder.filterFormatting(filterData);
  }

  onReady(e) {
    const grid = e.component;
    const selection = grid.getSelectedRowKeys();
    
    console.log('onReady called - selection length:', selection.length);
    console.log('Current selectedMappingRow:', this.selectedMappingRow);
    
    // ALWAYS select first row when Grid 1 data refreshes
    console.log('Forcing first row selection in onReady');
    grid.selectRowsByIndexes([0]);
    
    // Trigger selection change to update Grid 2
    setTimeout(() => {
      console.log('Triggering selectionChangedHandler after first row selection');
      this.selectionChangedHandler();
    }, 100);
  }


  refreshMappingDataGrid() {
    // Clear all filters first
    this.salonMappingGrid.instance.clearFilter();
    
    // Then refresh the grid
    this.salonMappingGrid.instance.refresh().then(() => {
      this.salonMappingGrid.instance.repaint();
    });
  }

  private refreshMappingDataGridPreserveFilters() {
    if (!this.salonMappingGrid?.instance) {
      return;
    }

    this.salonMappingGrid.instance.refresh().then(() => {
      this.salonMappingGrid.instance.repaint();
    });
  }

  private forceFirstRowSelectionAndUpdateGrid2() {
    console.log('forceFirstRowSelectionAndUpdateGrid2 called');
    
    if (this.salonMappingGrid?.instance) {
      // Force select first row
      this.salonMappingGrid.instance.selectRowsByIndexes([0]);
      
      // Wait a bit for selection to take effect, then update Grid 2
      setTimeout(() => {
        console.log('Forcing Grid 2 update after first row selection');
        this.selectionChangedHandler();
      }, 150);
    }
  }

  private loadColumnHeaderFilterData() {
    // Initialize header filter data sources with proper API calls
    this.mappingAssignedHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'MappingAssignedToUserFullNameSortableOrPrincipalName';
        loadOptions.valueColumn = '';
        return this.getHeaderFiltersSalonMapping(loadOptions);
      }
    });
    this.modifiedByHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'ModifiedByUserFullNameSortableOrPrincipalName';
        loadOptions.valueColumn = '';
        return this.getHeaderFiltersSalonMapping(loadOptions);
      }
    });
    this.reviewedByHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'ReviewedByUserFullNameSortableOrPrincipalName';
        loadOptions.valueColumn = '';
        return this.getHeaderFiltersSalonMapping(loadOptions);
      }
    });
    this.createdByHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'CreatedByUserFullNameSortableOrPrincipalName';
        loadOptions.valueColumn = '';
        return this.getHeaderFiltersSalonMapping(loadOptions);
      }
    });
    this.dataProviderNameHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'DataProviderName';
        loadOptions.valueColumn = '';
        return this.getHeaderFiltersSalonMapping(loadOptions);
      }
    });
    this.mappingPriorityHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'MappingPriority';
        loadOptions.valueColumn = '';
        return this.getHeaderFiltersSalonMapping(loadOptions);
      }
    });
    this.modifiedOnHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'ModifiedOn';
        loadOptions.valueColumn = '';
        return this.getHeaderFiltersSalonMapping(loadOptions);
      }
    });
    this.reviewedOnHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'ReviewedOn';
        loadOptions.valueColumn = '';
        return this.getHeaderFiltersSalonMapping(loadOptions);
      }
    });
    this.createdOnHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'CreatedOn';
        loadOptions.valueColumn = '';
        return this.getHeaderFiltersSalonMapping(loadOptions);
      }
    });

    // New header filter data sources for additional columns
    this.sourceCountryHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'SourceCountry';
        loadOptions.valueColumn = '';
        return this.getHeaderFiltersSalonMapping(loadOptions);
      }
    });
    this.sourceCountrySubdivisionHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'SourceCountrySubdivision';
        loadOptions.valueColumn = '';
        loadOptions.salonMappingId = this.selectedMappingRow?.SalonMappingId;
        return this.getHeaderFiltersSalonMappingToMaster(loadOptions);
      }
    });
    this.sourceSalonTypeHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'SourceSalonType';
        loadOptions.valueColumn = '';
        return this.getHeaderFiltersSalonMapping(loadOptions);
      }
    });
    this.dataProviderHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'DataProviderCode';
        loadOptions.valueColumn = '';
        loadOptions.salonMappingId = this.selectedMappingRow?.SalonMappingId;
        return this.getHeaderFiltersSalonMappingToMaster(loadOptions);
      }
    });
    this.primaryDataProviderHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'PrimaryDataProviderCode';
        loadOptions.valueColumn = '';
        return this.getHeaderFiltersSalonMaster(loadOptions);
      }
    });
    this.baseLevelGeographicalEntityHeaderDataSource = new CustomStore({
      load: (loadOptions: any) => {
        loadOptions.column = 'BaseLevelGeographicalEntityShortName';
        loadOptions.valueColumn = '';
        return this.getHeaderFiltersSalonMaster(loadOptions);
      }
    });

    // Mapping mode dropdown data source is already initialized in ngOnInit
  }

  private getHeaderFiltersSalonMapping(loadOptions: any): Promise<any> {
    return this.salonMappingService.getHeaderFiltersSalonMapping(loadOptions)
      .pipe(takeUntil(this.destroyed$))
      .toPromise()
      .catch(error => { 
        console.error('Header filter data loading error:', error);
        return []; 
      });
  }

  private getHeaderFiltersSalonMappingToMaster(loadOptions: any): Promise<any> {
    return this.salonMappingService.getHeaderFiltersSalonMappingToMaster(loadOptions)
      .pipe(takeUntil(this.destroyed$))
      .toPromise()
      .catch(error => { 
        console.error('Header filter data loading error for Grid 2:', error);
        return []; 
      });
  }

  private getHeaderFiltersSalonMaster(loadOptions: any): Promise<any> {
    return this.salonMappingService.getHeaderFiltersSalonMaster(loadOptions)
      .pipe(takeUntil(this.destroyed$))
      .toPromise()
      .catch(error => { 
        console.error('Header filter data loading error for Grid 3:', error);
        return []; 
      });
  }

  private loaderHandler() {
    if (
      !this.isSalonMappingDataLoading &&
      !this.isSalonMappingToMasterDataLoading &&
      !this.isSalonMasterDataLoading &&
      !this.isSalonMappingDataGridSettingLoading) {
      this.loaderService.hide();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}


